import { haversineDistanceKm, parseLatLngFromLocationString } from './geo';

export type InspectorDistanceDisplayInput = {
  travelDistance?: number | null;
  travelTime?: number | null;
  straightLineDistance?: number | null;
  inspectorLocation?: string | null;
  siteLocation?: string | null;
  isDeskService?: boolean;
};

export type InspectorDistanceDisplayI18n = {
  deskServiceZeroKm: string;
  travelDistance: (distanceLabel: string) => string;
  straightLineDistance: (distanceLabel: string) => string;
  travelTime: (timeLabel: string) => string;
  travelTimeHoursMinutes: (hours: number, minutes: number) => string;
  travelTimeHoursOnly: (hours: number) => string;
  travelTimeMinutesOnly: (minutes: number) => string;
};

const defaultInspectorDistanceI18n: InspectorDistanceDisplayI18n = {
  deskServiceZeroKm: '0.0 km',
  travelDistance: (distanceLabel) => `${distanceLabel} travel distance`,
  straightLineDistance: (distanceLabel) => `${distanceLabel} straight-line distance`,
  travelTime: (timeLabel) => `${timeLabel} travel time`,
  travelTimeHoursMinutes: (hours, minutes) => `${hours}hr ${minutes} min`,
  travelTimeHoursOnly: (hours) => `${hours}hr`,
  travelTimeMinutesOnly: (minutes) => `${minutes} min`,
};

export function formatInspectorDistanceKm(km: number | null | undefined): string | null {
  if (km == null || !Number.isFinite(km)) {
    return null;
  }
  const formatted = km < 10 ? km.toFixed(1) : String(Math.round(km));
  return `${formatted} km`;
}

export function formatInspectorTravelTimeLabel(
  seconds: number | null | undefined,
  i18n: Pick<
    InspectorDistanceDisplayI18n,
    'travelTimeHoursMinutes' | 'travelTimeHoursOnly' | 'travelTimeMinutesOnly'
  > = defaultInspectorDistanceI18n,
): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return i18n.travelTimeHoursMinutes(hours, minutes);
  }
  if (hours > 0) {
    return i18n.travelTimeHoursOnly(hours);
  }
  return i18n.travelTimeMinutesOnly(minutes);
}

export function resolveStraightLineDistanceKm({
  straightLineDistance,
  inspectorLocation,
  siteLocation,
}: Pick<
  InspectorDistanceDisplayInput,
  'straightLineDistance' | 'inspectorLocation' | 'siteLocation'
>): number | null {
  if (straightLineDistance != null && Number.isFinite(straightLineDistance)) {
    return straightLineDistance;
  }

  const inspectorCoords = parseLatLngFromLocationString(inspectorLocation);
  const siteCoords = parseLatLngFromLocationString(siteLocation);
  if (!inspectorCoords || !siteCoords) {
    return null;
  }

  return haversineDistanceKm(
    inspectorCoords.lat,
    inspectorCoords.lng,
    siteCoords.lat,
    siteCoords.lng,
  );
}

export function hasGoogleMapsTravelData(
  travelDistance?: number | null,
  travelTime?: number | null,
): boolean {
  return (
    travelTime != null &&
    Number.isFinite(travelTime) &&
    travelTime > 0 &&
    travelDistance != null &&
    Number.isFinite(travelDistance)
  );
}

export function getInspectorDistanceDisplay(
  {
    travelDistance,
    travelTime,
    straightLineDistance,
    inspectorLocation,
    siteLocation,
    isDeskService = false,
  }: InspectorDistanceDisplayInput,
  i18n: InspectorDistanceDisplayI18n = defaultInspectorDistanceI18n,
) {
  if (isDeskService) {
    return {
      primaryLabel: i18n.deskServiceZeroKm,
      tooltipLabel: null,
      hasGoogleMapsData: false,
    };
  }

  const resolvedStraightLine = resolveStraightLineDistanceKm({
    straightLineDistance,
    inspectorLocation,
    siteLocation,
  });
  const hasGoogleMapsData = hasGoogleMapsTravelData(travelDistance, travelTime);
  const primaryKm = hasGoogleMapsData
    ? travelDistance
    : resolvedStraightLine ?? travelDistance;
  const primaryLabel = formatInspectorDistanceKm(primaryKm);

  if (!hasGoogleMapsData) {
    return {
      primaryLabel,
      tooltipLabel: null,
      hasGoogleMapsData: false,
    };
  }

  const travelTimeLabel = formatInspectorTravelTimeLabel(travelTime, i18n);
  const routeDistanceLabel = formatInspectorDistanceKm(travelDistance);
  const straightLineLabel = formatInspectorDistanceKm(resolvedStraightLine);

  const tooltipParts = [
    routeDistanceLabel ? i18n.travelDistance(routeDistanceLabel) : null,
    straightLineLabel ? i18n.straightLineDistance(straightLineLabel) : null,
    travelTimeLabel ? i18n.travelTime(travelTimeLabel) : null,
  ].filter(Boolean);

  return {
    primaryLabel,
    tooltipLabel: tooltipParts.length > 0 ? tooltipParts.join(', ') : null,
    hasGoogleMapsData: true,
  };
}
